import React, { forwardRef, useCallback } from 'react';
import { useField } from 'formik';

// Types
interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface ISelectField {
  label?: string;
  value?: string;
  placeholder?: string;
  name: string;
  options: SelectOption[];
  labelJustify?: 'start' | 'space-between';
  labelColor?: 'default' | 'light';
  optional?: boolean;
  disabled?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLSelectElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLSelectElement>) => void;
  link?: {
    text: string;
    href: string;
  };
  help?: string;
  error?: string;
  touched?: boolean;
  required?: boolean;
  className?: string;
  extraMessage?: React.ReactNode;
}

// Base Select Field Component (can be used standalone)
const BaseSelectField = forwardRef<HTMLSelectElement, ISelectField>(
  (
    {
      label,
      value = '',
      placeholder,
      name,
      options,
      labelJustify = 'start',
      labelColor = 'default',
      optional = false,
      disabled = false,
      onChange,
      link,
      help = '',
      touched = false,
      error = '',
      required = false,
      className = '',
      extraMessage,
      onBlur,
      onFocus,
    },
    ref
  ) => {
    // Label classes
    const labelClasses = [
      'font-medium text-base mb-1',
      !disabled && labelColor === 'light' ? 'text-white' : '',
      !disabled && labelColor !== 'light' ? 'text-black' : '',
    ]
      .filter(Boolean)
      .join(' ');

    // Label box classes
    const labelBoxClasses = [
      'flex items-center gap-1',
      labelJustify === 'space-between' ? 'justify-between' : '',
    ]
      .filter(Boolean)
      .join(' ');

    // Select classes
    const selectClasses = [
      'form-field h-12 w-full px-3 py-2 bg-white text-base text-gray-850',
      'border border-gray-200 rounded',
      'focus:border-blue-300 focus:ring-1 focus:ring-blue-300 focus:outline-none',
      'hover:border-blue-300',
      'disabled:text-gray-500 disabled:cursor-not-allowed disabled:hover:border-gray-200',
      'appearance-none cursor-pointer',
      !value && placeholder ? 'text-gray-500' : '',
      error && touched
        ? 'border-red-500 focus:border-red-500 focus:ring-red-300'
        : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={className}>
        <div>
          {label && (
            <div className={labelBoxClasses}>
              <label
                htmlFor={name}
                className={labelClasses}
              >
                {label}{' '}
                {optional && <span className="text-gray-500">(optional)</span>}
              </label>
              {required && (
                <span className="text-red-500 text-sm font-medium">*</span>
              )}
              {help && (
                <span
                  className="inline-flex items-center justify-center w-4 h-4 text-xs text-gray-400 border border-gray-400 rounded-full cursor-help"
                  title={help}
                >
                  ?
                </span>
              )}

              {link && (
                <a
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 flex items-center ml-auto"
                >
                  <span className="font-normal text-xs leading-[18px] mr-2">
                    {link.text}
                  </span>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    className="text-blue-600"
                  >
                    <path
                      d="M6 3H3v10h10v-3"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M9 2h5v5M14 2l-6 6"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </a>
              )}
            </div>
          )}

          <div className="relative">
            <select
              ref={ref}
              id={name}
              name={name}
              value={value}
              className={selectClasses}
              onChange={onChange}
              disabled={disabled}
              onBlur={onBlur}
              onFocus={onFocus}
            >
              {placeholder && (
                <option
                  value=""
                  disabled
                >
                  {placeholder}
                </option>
              )}
              {options.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  className="w-full"
                >
                  {option.label}
                </option>
              ))}
            </select>
            {/* Dropdown arrow icon */}
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="4"
                className="text-gray-500"
              >
                <path
                  d="M6 9l6 6 6-6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>

          {touched && error && (
            <p
              className="text-red-500 text-sm mt-1"
              id={`${name}-error`}
            >
              {error}
            </p>
          )}

          {extraMessage && extraMessage}
        </div>
      </div>
    );
  }
);

BaseSelectField.displayName = 'BaseSelectField';

// Formik-compatible Select Field Component
type FormikSelectFieldProps = Omit<
  ISelectField,
  'value' | 'onChange' | 'error' | 'touched'
>;

const FormikSelectField = forwardRef<HTMLSelectElement, FormikSelectFieldProps>(
  ({ name, onBlur, onFocus, ...rest }, ref) => {
    const [{ value }, { touched, error }, { setValue, setTouched }] =
      useField<string>(name);

    const handleOnChange = useCallback(
      (e: React.ChangeEvent<HTMLSelectElement>) => {
        setValue(e.target.value, true);
      },
      [setValue]
    );

    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLSelectElement>) => {
        setTouched(true, true);
        onBlur?.(e);
      },
      [setTouched, onBlur]
    );

    return (
      <BaseSelectField
        ref={ref}
        name={name}
        value={value}
        onChange={handleOnChange}
        onBlur={handleBlur}
        onFocus={onFocus}
        touched={touched}
        error={error}
        {...rest}
      />
    );
  }
);

// Export both versions
export { BaseSelectField as SelectField, FormikSelectField };
export default FormikSelectField;
